// One motion, as reported.
//
// The order of the blocks below is the reading order on a 320px phone, and it is
// the order a person needs them in, not the order they were built in:
//
//   eyebrow → title → the instrument, verbatim → what the motion NEEDED →
//   the withheld card, if any decision is withheld → the tally →
//   the verdict, as a sentence → what the count reported.
//
// The withheld card comes BEFORE the tally because a reader who meets the
// numbers first has already formed a verdict by the time they reach the reason
// there isn't one. The verdict comes AFTER the tally because the arithmetic is
// the evidence and the sentence is the reading of it — and because the largest
// thing on this screen has to be the numbers (SO 14.1(k): the system reports; it
// never declares).
//
// The instrument is rendered verbatim and entire, with the ballot paper's own
// classes and through the ballot paper's own `instrumentParagraphs` (../paper):
// a member who could not read the whole motion could not consent to it, and a
// reader who cannot read the whole motion cannot check the report of it. One
// `<p>` per paragraph of the stored text, no truncation, no "show more", and no
// `dangerouslySetInnerHTML` anywhere near operator-entered prose.
//
// Moved and seconded are drawn ONLY where the record holds the name. Neither is
// substituted with an empty string, a dash or "not recorded": a motion may
// lawfully reach a ballot with neither recorded, and a results sheet that
// printed an attribution nobody made would be a falsehood in the one place that
// must not carry one. Same rule as the paper's (cast/MotionPaper.tsx).
//
// ── THERE IS NO BISHOP LINE, and this is where it used to be ────────────────
//
// A `<BishopVote>` component sat between the verdict and the issue list, and a
// `secrecy` prop was threaded down this component for that one reader: it gated
// whether the Bishop's CHOICE could be named, because he is one identified
// person and a one-person tally on a secret ballot publishes how one named man
// voted (SO 15.9). Registrar ruling 2026-08-16 removed the tally instead, and
// the component and the prop went with it. **A display suppression leaves the
// number in the payload for the next screen, the next export or the next
// developer to find; folding leaves no number to find.**
//
// So at engine version 2 there is no `MotionItemResult.bishop` and no
// `BishopTally` — nothing to render, and nothing for a secrecy gate to protect.
// His paper is INSIDE the clergy figures on a `present-and-voting` base, where
// the base is the votes themselves and he enters numerator and denominator in
// the same breath. On a base drawn from the roll (§15.2 / §18.1) it is excluded
// from every tally and reported as `bishop-vote-excluded` — an issue in the list
// at the foot of this component, in the same place as every other finding —
// because the roll seats him on his own rather than in a House, so counting his
// paper inside one would let his aye supply a majority that House did not give
// while his absence could never be the No the same clause makes it. A lawful
// elector cast a lawful paper and the Constitution gives it no House to land in:
// that is a fact about his standing, not a defect in the ballot.
//
// **Do not rebuild either the line or the gate.** If a House of Bishops is ever
// formed his votes come back out of the vote records by query — the `bishop`
// marker survives on every paper — and that is a new component with a new rule,
// not this one restored.

import { useId, type JSX } from 'react';
import type { CountIssue } from '@cca/decides-count';
import { instrumentParagraphs } from '../paper';
import { Yardstick } from '../Yardstick';
import { HouseTally } from './HouseTally';
import { IssueList } from './IssueList';
import { Verdict } from './Verdict';
import { MotionWithheldCard } from './WithheldCard';
import { withheldScopes, type MotionResultView } from './view';

interface MotionResultProps {
  view: MotionResultView;
  itemNumber: number;
  itemCount: number;
  issues: CountIssue[];
}

export function MotionResult({
  view,
  itemNumber,
  itemCount,
  issues,
}: MotionResultProps): JSX.Element {
  const { result } = view;
  const titleId = useId();
  const withheld = withheldScopes(result);
  // No name recorded on either side draws no list at all, rather than an empty
  // `<dl>` a screen reader still announces as a description list.
  const attributed = view.mover !== null || view.seconder !== null;

  return (
    <section className="card result-item" aria-labelledby={titleId}>
      <p className="eyebrow result-eyebrow">
        Item {itemNumber} of {itemCount} · Motion
      </p>
      <h2 className="result-item-title" id={titleId}>
        {view.title}
      </h2>

      <div className="result-instrument">
        {instrumentParagraphs(view.instrument).map((paragraph, index) => (
          // The text of a motion is fixed for the life of this report and its
          // paragraphs have no identity of their own, so the index is the honest
          // key: this list is never reordered, inserted into or filtered.
          <p className="ballot-instrument-text" key={index}>
            {paragraph}
          </p>
        ))}
        {attributed ? (
          <dl className="ballot-attrib">
            {view.mover !== null ? (
              <div className="ballot-attrib-row">
                <dt>Moved</dt>
                <dd>{view.mover}</dd>
              </div>
            ) : null}
            {view.seconder !== null ? (
              <div className="ballot-attrib-row">
                <dt>Second</dt>
                <dd>{view.seconder}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </div>

      {/* The same sentence, from the same table, as the paper the member marked
          — present tense, and not a results-only variant of it. */}
      <Yardstick base={result.base} level={3} />

      {withheld.length > 0 ? <MotionWithheldCard result={result} /> : null}

      <h3 className="result-subheading">
        {result.byHouses ? 'How the two Houses voted' : 'How Synod voted'}
      </h3>
      <HouseTally result={result} />
      <Verdict result={result} />

      <IssueList issues={issues} heading="What the count reported for this item" level={3} />
    </section>
  );
}
